/**
 * Simulator Page - Send test packets to clips
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useSessionsStore } from '../stores/sessionsStore';
import { useDiaperStore } from '../stores/diaperStore';
import { useSimulatorStore } from '../stores/simulatorStore';
import { useEnvironmentStore } from '../stores/environmentStore';
import { useToast } from '../stores/toastStore';
import { ClipSelector } from '../components/simulator/ClipSelector';
import { PhaseSelector } from '../components/simulator/PhaseSelector';
import { QuickActions } from '../components/simulator/QuickActions';
import { CustomPacketBuilder } from '../components/simulator/CustomPacketBuilder';
import { PacketLog } from '../components/simulator/PacketLog';
import { SessionInfoCard } from '../components/SessionInfoCard';
import { SiteTreeSelect } from '../components/SiteTreeSelect';
import { cn } from '../lib/utils';
import { loadSessions as apiLoadSessions } from '../lib/api';
import type { Session } from '../lib/types';
import {
  ConfigAccount,
  accountDisplayLabel,
  buildAvailableSiteIds,
  extractSiteIds,
  loadAccountSiteTree,
  loadEnabledConfigAccounts,
  resolveNovaAccountIdForSite,
} from '../lib/novaAccounts';
import type { SiteTreeNode } from '../lib/siteTree';
import {
  buildSiteNameMap,
  buildVisibleSiteRows,
  siteLabel,
} from '../lib/siteTree';

export const SimulatorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session');
  const initialAccountParam = searchParams.get('account') || '';
  
  const { features } = useEnvironmentStore();
  const authSites = useAuthStore((state) => state.sites);
  const {
    activeSessions,
    currentSite,
    currentNovaAccountId,
    setCurrentSite,
    setCurrentNovaAccountId,
    clearSessions,
    loadSessions,
  } = useSessionsStore();
  const { getDiaperModel, loadDiaperModels } = useDiaperStore();
  const {
    selectedClip,
    currentCriterion,
    currentPhase,
    changeNeededPercent,
    selectClip,
    updateSelectedClipFromSession,
    loadCriterion,
  } = useSimulatorStore();
  const toast = useToast();

  const [configAccounts, setConfigAccounts] = useState<ConfigAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [siteTree, setSiteTree] = useState<SiteTreeNode[]>([]);
  const [siteTreeLoading, setSiteTreeLoading] = useState(false);
  const [siteTreeError, setSiteTreeError] = useState<string | null>(null);
  const [accountSessions, setAccountSessions] = useState<Session[]>([]);
  const [accountSessionsLoading, setAccountSessionsLoading] = useState(false);
  const [accountSessionsError, setAccountSessionsError] = useState<string | null>(null);

  const selectedSession = activeSessions.find((s) => s.id === selectedClip?.id);
  const diaperModel = selectedClip?.diaperModelCode ? getDiaperModel(selectedClip.diaperModelCode) : null;
  const activeAccountId = selectedAccountId || currentNovaAccountId || '';
  const siteNamesById = useMemo(() => buildSiteNameMap(siteTree), [siteTree]);
  const availableSiteIds = useMemo(
    () => buildAvailableSiteIds(authSites, configAccounts, selectedAccountId, siteTree),
    [authSites, configAccounts, selectedAccountId, siteTree]
  );
  const siteRows = useMemo(
    () => buildVisibleSiteRows(siteTree, availableSiteIds),
    [availableSiteIds, siteTree]
  );
  const accountScopedSiteIds = useMemo(() => {
    if (!activeAccountId) return [];
    const account = configAccounts.find((item) => item.id === activeAccountId);
    const accountSites = extractSiteIds(account);
    if (accountSites.length > 0) return accountSites;
    return selectedSiteId ? [selectedSiteId] : [];
  }, [activeAccountId, configAccounts, selectedSiteId]);
  const duplicateSessions = useMemo(() => {
    if (!selectedClip?.clipAddress) return [];
    const selectedAddress = selectedClip.clipAddress.toUpperCase();
    return accountSessions.filter((session) => (
      session.id !== selectedClip.id
      && session.clipAddress?.toUpperCase() === selectedAddress
      && session.monitored !== false
    ));
  }, [accountSessions, selectedClip]);

  const loadConfigAccounts = useCallback(async () => {
    try {
      const enabledAccounts = await loadEnabledConfigAccounts();
      setConfigAccounts(enabledAccounts);
    } catch {}
  }, []);

  const loadAccountSessions = useCallback(async () => {
    if (!activeAccountId || accountScopedSiteIds.length === 0) {
      setAccountSessions([]);
      setAccountSessionsError(null);
      return;
    }

    setAccountSessionsLoading(true);
    setAccountSessionsError(null);
    try {
      const data = await apiLoadSessions(accountScopedSiteIds, { novaAccountId: activeAccountId });
      setAccountSessions(data.statuses || []);
    } catch (loadError: any) {
      console.error('Failed to load account sessions for duplicate detection:', loadError);
      setAccountSessions([]);
      setAccountSessionsError(loadError?.message || 'Failed to check duplicate clips');
    } finally {
      setAccountSessionsLoading(false);
    }
  }, [accountScopedSiteIds, activeAccountId]);

  useEffect(() => {
    loadConfigAccounts();
    const refreshTimer = window.setInterval(() => {
      loadConfigAccounts();
    }, 10000);

    return () => {
      window.clearInterval(refreshTimer);
    };
  }, [loadConfigAccounts]);

  useEffect(() => {
    if (!activeAccountId) {
      setSiteTree([]);
      setSiteTreeError(null);
      setSiteTreeLoading(false);
      return;
    }

    let cancelled = false;
    setSiteTreeLoading(true);
    setSiteTreeError(null);
    loadAccountSiteTree(activeAccountId)
      .then((tree) => {
        if (!cancelled) setSiteTree(tree);
      })
      .catch((loadError) => {
        if (cancelled) return;
        console.error('Failed to load Nova organization tree:', loadError);
        setSiteTree([]);
        setSiteTreeError(loadError?.message || 'Failed to load organization names');
      })
      .finally(() => {
        if (!cancelled) setSiteTreeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeAccountId]);

  useEffect(() => {
    loadAccountSessions();
  }, [loadAccountSessions]);

  useEffect(() => {
    if (!initialAccountParam) return;
    if (!configAccounts.some((account) => account.id === initialAccountParam)) return;
    setSelectedAccountId((current) => current || initialAccountParam);
  }, [configAccounts, initialAccountParam]);

  useEffect(() => {
    if (!selectedAccountId) return;
    if (configAccounts.some(account => account.id === selectedAccountId)) return;

    setSelectedAccountId('');
    setCurrentSite(null);
    clearSessions();
    selectClip(null);
  }, [clearSessions, configAccounts, selectClip, selectedAccountId, setCurrentSite]);

  useEffect(() => {
    const resolvedAccountId = resolveNovaAccountIdForSite(
      configAccounts,
      selectedAccountId,
      currentSite
    );
    setCurrentNovaAccountId(resolvedAccountId);
  }, [configAccounts, currentSite, selectedAccountId, setCurrentNovaAccountId]);

  useEffect(() => {
    return () => {
      setCurrentNovaAccountId(null);
    };
  }, [setCurrentNovaAccountId]);

  // Load the Nova diaper-models catalog once we know which account to drive.
  // The store caches per account and no-ops on repeat calls.
  // NOTE: `toast` is intentionally omitted from the dep list — useToast()
  // returns a fresh object every render, which would re-fire this effect
  // and loop with any setState the loader does.
  useEffect(() => {
    if (!currentNovaAccountId) return;
    loadDiaperModels(currentNovaAccountId).catch((err) => {
      toast.error(`Failed to load diaper models: ${err?.message || err}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNovaAccountId, loadDiaperModels]);

  // Load the criterion for the currently selected clip — but only after the
  // Nova account is known. selectClip used to fire this implicitly which
  // raced the URL-driven account resolver and silently disabled the
  // "Change Needed" button on direct deep-links.
  // Depend on the *ids*, not the whole `selectedClip` object — the periodic
  // session refresh creates a new object reference even when nothing
  // criterion-relevant changed, which would needlessly re-fetch.
  const selectedClipId = selectedClip?.id ?? null;
  const selectedCriterionId = selectedClip?.rawSession?.criterionId ?? null;
  useEffect(() => {
    if (!selectedClipId || !currentNovaAccountId || !selectedCriterionId) return;
    loadCriterion(selectedCriterionId).catch((err) => {
      toast.error(`Failed to load criterion: ${err?.message || err}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClipId, selectedCriterionId, currentNovaAccountId, loadCriterion]);

  useEffect(() => {
    if (selectedSiteId == null) return;
    if (availableSiteIds.includes(selectedSiteId)) return;

    setSelectedSiteId(null);
    setCurrentSite(null);
    clearSessions();
    selectClip(null);
  }, [availableSiteIds, clearSessions, selectClip, selectedSiteId, setCurrentSite]);

  // Auto-select first site when available
  useEffect(() => {
    if (availableSiteIds.length > 0 && !selectedSiteId) {
      const firstSite = availableSiteIds[0];
      setSelectedSiteId(firstSite);
      setCurrentSite(firstSite);
    }
  }, [availableSiteIds, selectedSiteId, setCurrentSite]);

  // Load sessions when site changes
  useEffect(() => {
    if (selectedSiteId && currentSite === selectedSiteId) {
      loadSessions();
    }
  }, [selectedSiteId, currentSite, loadSessions]);

  const handleSiteChange = (siteId: number) => {
    if (siteId !== selectedSiteId) {
      selectClip(null);
      clearSessions();
    }
    setSelectedSiteId(siteId);
    setCurrentSite(siteId);
  };

  const handleAccountChange = (accountId: string) => {
    selectClip(null);
    clearSessions();
    setSelectedAccountId(accountId);
    setSelectedSiteId(null);
    setCurrentSite(null);
  };

  useEffect(() => {
    if (sessionIdParam && activeSessions.length > 0) {
      const sessionId = Number(sessionIdParam);
      activeSessions.find((s) => s.id === sessionId);
    }
  }, [sessionIdParam, activeSessions]);

  useEffect(() => {
    if (selectedClip && activeSessions.length > 0) {
      const updatedSession = activeSessions.find((s) => s.id === selectedClip.id);
      if (updatedSession) {
        const hasChanged = 
          (updatedSession.wetnessLevel || 0) !== selectedClip.wetnessLevel ||
          (updatedSession.statusCode || 0) !== selectedClip.statusCode ||
          (updatedSession.clipBatteryPercent || 0) !== selectedClip.clipBatteryPercent ||
          (updatedSession.diaperCounter || 0) !== selectedClip.diaperCounter;
        
        if (hasChanged) {
          const updatedClip = {
            id: updatedSession.id,
            clipAddress: updatedSession.clipAddress,
            clipName: updatedSession.clipName,
            client: updatedSession.client,
            diaperModelCode: updatedSession.diaperModelCode || null,
            away: updatedSession.away || false,
            monitored: updatedSession.monitored,
            powerDownTime: updatedSession.powerDownTime || null,
            diaperId: updatedSession.diaperId || null,
            diaperCounter: updatedSession.diaperCounter || 0,
            firmwareRevision: updatedSession.firmwareRevision || 0,
            clipBatteryPercent: updatedSession.clipBatteryPercent || 0,
            wetnessLevel: updatedSession.wetnessLevel || 0,
            statusCode: updatedSession.statusCode || 0,
            adcValues: updatedSession.adcValues || null,
            rawSession: updatedSession,
          };
          updateSelectedClipFromSession(updatedSession.id, updatedClip);
        }
      }
    }
  }, [activeSessions, selectedClip, updateSelectedClipFromSession]);

  if (!features.canSimulate) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-3">Simulator Disabled</h2>
          <p className="text-gray-500">
            The Clip Simulator is disabled in production mode to prevent test events from being sent to the production Nova backend.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">Clip Simulator</h2>
        
        <p className="text-gray-600 mb-4 bg-blue-50 border-l-4 border-primary p-3 rounded">
          🔧 <strong>Simulate sensor data</strong> from a clip attached to a session. The payloads you
          create here are delivered directly to the backend system for processing—
          <em>not</em> sent via Bluetooth. This allows you to test wetness alerts, battery warnings, and
          other events without physical hardware.
          <br />
          <br />
          💡 <strong>Tip:</strong> Select an account and site below, then pick a session to enable the simulator controls.
        </p>

        {/* Account and Site selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Nova Account</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={selectedAccountId}
              onChange={e => handleAccountChange(e.target.value)}
            >
              <option value="">All accounts</option>
              {configAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {accountDisplayLabel(a)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Site ID</label>
            <div className="flex gap-2">
              <SiteTreeSelect
                className="flex-1"
                rows={siteRows}
                value={selectedSiteId}
                onChange={handleSiteChange}
                emptyLabel="No sites available"
                disabled={availableSiteIds.length === 0}
              />
              <button
                onClick={() => {
                  loadConfigAccounts();
                  loadAccountSessions();
                  if (currentSite) {
                    loadSessions();
                  }
                }}
                className="px-3 py-2 border rounded-lg text-xs text-gray-600 hover:bg-gray-50"
              >
                Refresh
              </button>
            </div>
            {selectedSiteId && (
              <p className="mt-1 text-xs text-gray-500">
                {siteTreeLoading
                  ? 'Loading site names...'
                  : `Selected: ${siteLabel(selectedSiteId, siteNamesById)}`}
              </p>
            )}
            {siteTreeError && (
              <p className="mt-1 text-xs text-amber-700">{siteTreeError}</p>
            )}
            {accountSessionsError && (
              <p className="mt-1 text-xs text-amber-700">{accountSessionsError}</p>
            )}
          </div>
        </div>

        <ClipSelector
          defaultSessionId={sessionIdParam ? Number(sessionIdParam) : null}
          siteNames={siteNamesById}
          duplicateSessions={duplicateSessions}
          checkingDuplicateClips={accountSessionsLoading}
          onRefreshAccountSessions={loadAccountSessions}
        />

        {/* Selected Session Info Card */}
        {selectedSession && selectedClip && (
          <div className="mt-4">
            <SessionInfoCard
              session={selectedSession}
              criterion={currentCriterion}
              phase={currentPhase}
              changeNeededPercent={changeNeededPercent}
              diaperModel={diaperModel}
              showActions
              activeSessions={accountSessions.length > 0 ? accountSessions : activeSessions}
              onActionComplete={() => {
                loadSessions();
                loadAccountSessions();
              }}
            />
          </div>
        )}

        {/* Phase Selector - Only shows if multiple phases available */}
        {selectedClip && <PhaseSelector />}
      </div>

      {/* Simulator Content */}
      <div
        className={cn(
          'relative transition-opacity',
          !selectedClip && 'opacity-40 pointer-events-none'
        )}
      >
        {!selectedClip && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 bg-white px-8 py-4 rounded-lg shadow-lg pointer-events-auto">
            <p className="text-lg font-semibold text-yellow-600">
              ⚠️ Please select a session to enable simulator controls
            </p>
          </div>
        )}

        <QuickActions />
        <CustomPacketBuilder />
        <PacketLog />
      </div>
    </div>
  );
};
